import { useEffect, useRef } from "preact/hooks"
import { ForecastConfirmationAction, ForecastEditPreview, ForecastRemovedProduct, Message } from "../../api/forecast_chat"
import { Wand2 } from "lucide-react"
import { marked } from "marked"
import { format, parseISO } from "date-fns"
import { Button } from "../ui/button"

interface MessageListProps {
  messages: Message[]
  loading?: boolean
  confirmingAction?: boolean
  streamingStatus?: string
  onConfirmAction?: (action: ForecastConfirmationAction) => void
  className?: string
}

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
})

function escape_html(html: string): string {
  return html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

/**
 * Allowlist of link targets a rendered message may point at. marked does not filter link
 * protocols (its url cleaning only `encodeURI`s), so without this `[click](javascript:alert(1))`
 * reaches `dangerouslySetInnerHTML` as a clickable script URL. An allowlist rather than a
 * blocklist because the href is written verbatim into the attribute and the browser decodes
 * entities there — `&#106;avascript:` would slip past a "does it start with javascript:" test.
 */
export function sanitize_markdown_href(href: string | null | undefined): string {
  const value = (href ?? "").trim().replace(/[\u0000-\u0020]/g, "")
  if (value.startsWith("#") || value.startsWith("/") || value.startsWith("./") || value.startsWith("../")) {
    return value
  }
  return /^(https?:\/\/|mailto:)/i.test(value) ? value : "#"
}

// Escape raw HTML tokens and neutralize unsafe link/image targets: assistant replies are
// model-generated from data that can carry attacker-influenced text (imported file cells, node
// names), are persisted, and re-render through dangerouslySetInnerHTML on every load — passing
// raw HTML or a `javascript:` href through would be stored XSS.
marked.use({
  renderer: {
    html(token: { text: string }) {
      return escape_html(token.text)
    },
  },
  walkTokens(token: { type: string; href?: string }) {
    if (token.type === "link" || token.type === "image") {
      token.href = sanitize_markdown_href(token.href)
    }
  },
})

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("markdown link sanitization", () => {
    it("drops script-bearing hrefs, including entity- and whitespace-obfuscated ones", () => {
      expect(sanitize_markdown_href("javascript:alert(1)")).toBe("#")
      expect(sanitize_markdown_href("JaVaScRiPt:alert(1)")).toBe("#")
      expect(sanitize_markdown_href("java\tscript:alert(1)")).toBe("#")
      expect(sanitize_markdown_href("&#106;avascript:alert(1)")).toBe("#")
      expect(sanitize_markdown_href("data:text/html,<script>alert(1)</script>")).toBe("#")
      expect(sanitize_markdown_href("vbscript:msgbox(1)")).toBe("#")
      expect(sanitize_markdown_href(undefined)).toBe("#")
    })

    it("keeps the link targets a real answer uses", () => {
      expect(sanitize_markdown_href("https://marginwiz.com/forecast")).toBe("https://marginwiz.com/forecast")
      expect(sanitize_markdown_href("http://example.com")).toBe("http://example.com")
      expect(sanitize_markdown_href("mailto:ops@marginwiz.com")).toBe("mailto:ops@marginwiz.com")
      expect(sanitize_markdown_href("/sales-forecasting")).toBe("/sales-forecasting")
      expect(sanitize_markdown_href("#totals")).toBe("#totals")
    })

    it("renders no javascript: url through the html sink", () => {
      const html = marked.parse("[click](javascript:alert(1)) ![x](javascript:alert(2))") as string
      expect(html).not.toContain("javascript:")
      expect(html).toContain('href="#"')
    })

    it("still escapes raw html tokens", () => {
      const html = marked.parse("<img src=x onerror=alert(1)>") as string
      expect(html).not.toContain("<img")
      expect(html).toContain("&lt;img")
    })
  })
}

export function parseForecastAction(content: string): { visibleContent: string; action: ForecastConfirmationAction | null } {
  const match = content.match(/```forecast-action\s*([\s\S]*?)```/)
  if (!match) {
    return { visibleContent: content, action: null }
  }

  try {
    return {
      visibleContent: content.replace(match[0], "").trim(),
      action: JSON.parse(match[1]) as ForecastConfirmationAction,
    }
  } catch (error) {
    console.error("Failed to parse forecast action:", error)
    return { visibleContent: content, action: null }
  }
}

function renderMessageContent(content: string, isUser: boolean) {
  if (isUser) {
    // User messages are plain text
    return <div class="whitespace-pre-wrap break-words">{content}</div>
  }
  
  // AI messages are rendered as markdown
  const html = marked.parse(content) as string
  return (
    <div
      class="markdown-content break-words"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

interface MessageItemProps {
  message: Message
  confirmingAction?: boolean
  onConfirmAction?: (action: ForecastConfirmationAction) => void
}

function MessageItem({ message, confirmingAction, onConfirmAction }: MessageItemProps) {
  const isUser = message.role === "user"
  const parsed = isUser ? { visibleContent: message.content, action: null } : parseForecastAction(message.content)
  
  return (
      <div class={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
        <div
          class={`max-w-[85%] rounded-lg px-4 py-3 ${
            isUser 
              ? "bg-primary text-primary-foreground" 
              : "bg-white border border-gray-200 text-gray-900 shadow-sm"
          }`}
        >
          <div class="text-sm">
            {renderMessageContent(parsed.visibleContent, isUser)}
            {parsed.action && (
              <ForecastActionCard
                action={parsed.action}
                confirming={confirmingAction}
                onConfirm={onConfirmAction}
              />
            )}
          </div>
          <div
            class={`text-xs mt-2 ${
              isUser ? "text-primary-foreground/80" : "text-gray-500"
            }`}
          >
            {format(parseISO(message.created_at), "h:mm:ss a")}
          </div>
        </div>
      </div>
  )
}

interface ForecastActionCardProps {
  action: ForecastConfirmationAction
  confirming?: boolean
  onConfirmAction?: (action: ForecastConfirmationAction) => void
  onConfirm?: (action: ForecastConfirmationAction) => void
}

function ForecastActionCard({ action, confirming, onConfirm }: ForecastActionCardProps) {
  const summary = getForecastActionSummary(action)
  return (
    <div class="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
      <div class="flex items-start gap-2">
        <Wand2 className="mt-0.5 h-4 w-4 text-blue-700" />
        <div class="min-w-0 flex-1">
          <div class="text-sm font-semibold text-blue-950">{action.label}</div>
          <div class="mt-1 text-xs text-blue-800">
            Review this proposed forecast action before applying it. The server will re-validate the change first.
          </div>
          <div class="mt-3 rounded-md bg-white/80 p-3">
            <div class="text-sm font-medium text-blue-950">{summary.title}</div>
            {summary.details.length > 0 && (
              <div class="mt-2 space-y-1 text-xs text-blue-900">
                {summary.details.map((detail: string) => (
                  <div key={detail}>{detail}</div>
                ))}
              </div>
            )}
            {summary.examples.length > 0 && (
              <div class="mt-3">
                <div class="text-xs font-medium uppercase tracking-wide text-blue-700">Examples</div>
                <div class="mt-1 space-y-1 text-xs text-blue-950">
                  {summary.examples.map((example: string) => (
                    <div key={example}>{example}</div>
                  ))}
                </div>
              </div>
            )}
            {summary.warnings.length > 0 && (
              <div class="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                {summary.warnings.slice(0, 3).map((warning: string) => (
                  <div key={warning}>{warning}</div>
                ))}
              </div>
            )}
          </div>
          <Button
            size="sm"
            className="mt-3"
            disabled={confirming}
            onClick={() => onConfirm?.(action)}
          >
            {confirming ? "Applying..." : "Apply Forecast Action"}
          </Button>
        </div>
      </div>
    </div>
  )
}

interface ForecastActionSummary {
  title: string
  details: string[]
  examples: string[]
  warnings: string[]
}

function getForecastActionSummary(action: ForecastConfirmationAction): ForecastActionSummary {
  const preview = action.preview
  if (preview?.action === "forecast_edit" || preview?.edits?.length) {
    const edits = preview.edits || []
    return {
      title: `Apply ${preview.edit_count ?? edits.length} forecast edit${(preview.edit_count ?? edits.length) === 1 ? "" : "s"}`,
      details: [
        `Total change: ${formatCurrency(preview.volume_delta)}`,
        `Before: ${formatCurrency(preview.before_volume_dollars)} -> After: ${formatCurrency(preview.after_volume_dollars)}`,
      ],
      examples: edits.slice(0, 4).map((edit: ForecastEditPreview) => {
        return `${edit.row_name || edit.path_key || "Forecast row"} in ${edit.period}: ${formatCurrency(edit.before?.volume_dollars)} -> ${formatCurrency(edit.after?.volume_dollars)}`
      }),
      warnings: preview.warnings || [],
    }
  }

  if (preview?.action === "remove_products" || action.action_type === "remove_products") {
    const products = preview?.products || []
    const rows = preview?.rows_removed ?? 0
    const customers = preview?.customers_affected ?? 0
    const opportunity_dollars = Number(preview?.total_opportunity_dollars || 0)
    return {
      title: `Remove ${products.length} product${products.length === 1 ? "" : "s"} from ${rows.toLocaleString()} row${rows === 1 ? "" : "s"} across ${customers.toLocaleString()} customer${customers === 1 ? "" : "s"}`,
      details: [
        `Revenue removed: ${formatCurrency(preview?.total_volume_dollars)}${
          opportunity_dollars > 0 ? ` (${formatCurrency(opportunity_dollars)} of it weighted opportunities)` : ""
        }`,
        `Volume removed: ${formatCount(preview?.total_cases)} cases, ${formatCount(preview?.total_units)} units`,
        "This deletes the rows. It can be restored from the forecast changelog.",
      ],
      examples: products.map((product: ForecastRemovedProduct) => {
        const span = product.first_period && product.last_period ? `, ${product.first_period} to ${product.last_period}` : ""
        return `${product.name}${product.upc ? ` (${product.upc})` : ""}: ${product.rows.toLocaleString()} row${product.rows === 1 ? "" : "s"}, ${formatCurrency(product.volume_dollars)}${span}`
      }),
      warnings: preview?.warnings || [],
    }
  }

  if (preview?.action === "clone_forecast_version" || action.action_type === "clone_forecast_version") {
    return {
      title: `Create "${preview?.new_version_name || action.label}"`,
      details: [
        `Source: ${preview?.source_version_name || "selected forecast version"}`,
        `Scenario: ${preview?.scenario_kind || "same as source"}`,
      ],
      examples: [],
      warnings: [],
    }
  }

  return {
    title: action.label,
    details: ["The server will validate the proposed action before applying it."],
    examples: [],
    warnings: [],
  }
}

function formatCurrency(value: unknown): string {
  const numeric = typeof value === "number" ? value : Number(value || 0)
  return numeric.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  })
}

function formatCount(value: unknown): string {
  const numeric = typeof value === "number" ? value : Number(value || 0)
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function LoadingSpinner() {
  return <span class="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin" />
}

export function MessageList({ messages, loading, confirmingAction, streamingStatus, onConfirmAction, className }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, loading])

  return (
    <>
      <style>{`
        .markdown-content {
          line-height: 1.6;
        }
        .markdown-content h1,
        .markdown-content h2,
        .markdown-content h3,
        .markdown-content h4 {
          font-weight: 600;
          margin-top: 1em;
          margin-bottom: 0.5em;
        }
        .markdown-content h1 { font-size: 1.5em; }
        .markdown-content h2 { font-size: 1.3em; }
        .markdown-content h3 { font-size: 1.1em; }
        .markdown-content h4 { font-size: 1em; }
        .markdown-content p {
          margin-bottom: 0.75em;
        }
        .markdown-content ul,
        .markdown-content ol {
          margin-left: 1.5em;
          margin-bottom: 0.75em;
        }
        .markdown-content li {
          margin-bottom: 0.25em;
        }
        .markdown-content code {
          background-color: rgba(0, 0, 0, 0.05);
          padding: 0.125em 0.25em;
          border-radius: 0.25em;
          font-size: 0.9em;
          font-family: monospace;
        }
        .markdown-content pre {
          background-color: rgba(0, 0, 0, 0.05);
          padding: 0.75em;
          border-radius: 0.5em;
          overflow-x: auto;
          margin: 0.75em 0;
        }
        .markdown-content pre code {
          background-color: transparent;
          padding: 0;
        }
        .markdown-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.75em 0;
        }
        .markdown-content table th,
        .markdown-content table td {
          border: 1px solid rgba(0, 0, 0, 0.1);
          padding: 0.5em;
          text-align: left;
        }
        .markdown-content table th {
          background-color: rgba(0, 0, 0, 0.05);
          font-weight: 600;
        }
        .markdown-content strong {
          font-weight: 600;
        }
        .markdown-content em {
          font-style: italic;
        }
        .markdown-content blockquote {
          border-left: 3px solid rgba(0, 0, 0, 0.2);
          padding-left: 1em;
          margin: 0.75em 0;
          color: rgba(0, 0, 0, 0.7);
        }
      `}</style>
      <div class={`flex flex-col p-4 ${className || ""}`}>
        {messages.length === 0 && !loading && (
          <div class="flex items-center justify-center flex-1 text-gray-500">
            <p>No messages yet. Start a conversation!</p>
          </div>
        )}
        {messages.map((message) => (
          <MessageItem
            key={message.id}
            message={message}
            confirmingAction={confirmingAction}
            onConfirmAction={onConfirmAction}
          />
        ))}
        {loading && (
          <div class="flex justify-start mb-4">
            <div class="bg-gray-100 rounded-lg px-4 py-2 flex items-center gap-2">
              <LoadingSpinner />
              <span class="text-sm text-gray-600">{streamingStatus || "Thinking..."}</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("parseForecastAction", () => {
    it("extracts forecast confirmation actions from assistant content", () => {
      const parsed = parseForecastAction(`Preview ready.

\`\`\`forecast-action
{"action_type":"forecast_edit","label":"Apply edits","params":{"version_id":"v1"}}
\`\`\``)

      expect(parsed.visibleContent).toBe("Preview ready.")
      expect(parsed.action?.action_type).toBe("forecast_edit")
      expect(parsed.action?.params.version_id).toBe("v1")
    })

    it("summarizes forecast edit previews without exposing raw JSON", () => {
      const summary = getForecastActionSummary({
        action_type: "forecast_edit",
        label: "Apply forecast edits",
        params: {},
        preview: {
          action: "forecast_edit",
          edit_count: 1,
          before_volume_dollars: "50",
          after_volume_dollars: "100",
          volume_delta: "50",
          edits: [{
            row_name: "KeHE Sparkling Water",
            period: "2026-07",
            before: { volume_dollars: "50" },
            after: { volume_dollars: "100" },
          }],
        },
      })

      expect(summary.title).toBe("Apply 1 forecast edit")
      expect(summary.examples[0]).toContain("KeHE Sparkling Water")
      expect(summary.examples[0]).toContain("$50")
      expect(summary.examples[0]).toContain("$100")
    })

    it("lists every product a removal deletes, with what it was worth", () => {
      const summary = getForecastActionSummary({
        action_type: "remove_products",
        label: "Remove 2 products from the forecast (64 rows)",
        params: {},
        preview: {
          action: "remove_products",
          rows_removed: 64,
          customers_affected: 38,
          total_volume_dollars: "675569.65",
          total_opportunity_dollars: "11916.34",
          total_cases: 49794,
          total_units: "597860",
          warnings: ["Nothing matched \"Mango\" — it is not part of this removal."],
          products: [
            { name: "Paradise Punch Sparkling Water (SS)", upc: "840269200342", rows: 38, customers: 38, volume_dollars: "425207.45", first_period: "2026-W01", last_period: "2027-W52" },
            { name: "Cherry Golden Lime Sparkling Water (SS)", upc: "840269200480", rows: 26, customers: 26, volume_dollars: "262278.54" },
          ],
        },
      })

      expect(summary.title).toBe("Remove 2 products from 64 rows across 38 customers")
      expect(summary.details[0]).toBe("Revenue removed: $675,570 ($11,916 of it weighted opportunities)")
      expect(summary.details[1]).toBe("Volume removed: 49,794 cases, 597,860 units")
      expect(summary.examples).toEqual([
        "Paradise Punch Sparkling Water (SS) (840269200342): 38 rows, $425,207, 2026-W01 to 2027-W52",
        "Cherry Golden Lime Sparkling Water (SS) (840269200480): 26 rows, $262,279",
      ])
      expect(summary.warnings).toHaveLength(1)
    })
  })
}

